import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy 
} from 'firebase/firestore';
import { 
  sendPasswordResetEmail
} from 'firebase/auth';
import { db, auth } from '../../firebase';
import TransactionLogger from '../../utils/transactionLogger';
import { hasPermission } from '../../utils/permissions';
import UserManagementUtils from '../../utils/userManagement';
import { checkClymusaStatus } from '../../utils/makeUserAdmin';
import { makeAllUsersAdmin, revertAllUsersToOrganizer, getUsersAdminStatus } from '../../utils/tempMakeAllUsersAdmin';
import { formatUserName, formatUserRole, formatUserEmail, getEmergencyAdminWarning } from '../../utils/userDisplayUtils';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Lock,
  LockReset,
  Person,
  Email,
  Business,
  Security,
  Refresh
} from '@mui/icons-material';

export default function UserManagement() {
  const { userProfile, currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organizationName: '',
    role: 'organizer'
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Check if user has permission to manage users
  const canManageUsers = hasPermission(userProfile?.role, 'MANAGE_USERS');
  const canCreateUser = hasPermission(userProfile?.role, 'CREATE_USER');
  const canEditUser = hasPermission(userProfile?.role, 'EDIT_USER');
  const canDeleteUser = hasPermission(userProfile?.role, 'DELETE_USER');
  const canChangePassword = hasPermission(userProfile?.role, 'CHANGE_USER_PASSWORD');
  const canSendPasswordReset = hasPermission(userProfile?.role, 'SEND_PASSWORD_RESET');

  useEffect(() => {
    if (canManageUsers) {
      fetchUsers();
    }
  }, [canManageUsers]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ type: 'error', text: 'Error fetching users: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      organizationName: '',
      role: 'organizer'
    });
    setMessage({ type: '', text: '' });
    setOpenDialog(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      organizationName: user.organizationName || '',
      role: user.role || 'organizer'
    });
    setMessage({ type: '', text: '' });
    setOpenDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!canCreateUser && !canEditUser) {
      setMessage({ type: 'error', text: 'You do not have permission to manage users' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      if (editingUser) {
        // Update existing user
        await updateDoc(doc(db, 'users', editingUser.id), {
          ...formData,
          updatedAt: new Date()
        });

        await TransactionLogger.logTransaction(
          'UPDATE',
          'USER',
          editingUser.id,
          editingUser,
          formData,
          currentUser.uid,
          currentUser.email
        );

        setMessage({ type: 'success', text: 'User updated successfully!' });
      } else {
        // Create new user - this would require Firebase Admin SDK
        // For now, we'll show an error message
        setMessage({ type: 'error', text: 'User creation requires Firebase Admin SDK. Please use the signup page.' });
        setOpenDialog(false);
        return;
      }

      await fetchUsers();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error managing user:', error);
      setMessage({ type: 'error', text: 'Error managing user: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!canDeleteUser) {
      setMessage({ type: 'error', text: 'You do not have permission to delete users' });
      return;
    }

    if (window.confirm(`Are you sure you want to delete user ${user.email}? This action cannot be undone.`)) {
      try {
        setLoading(true);
        
        // Delete from Firestore
        await deleteDoc(doc(db, 'users', user.id));

        await TransactionLogger.logTransaction(
          'DELETE',
          'USER',
          user.id,
          user,
          null,
          currentUser.uid,
          currentUser.email
        );

        setMessage({ type: 'success', text: 'User deleted successfully!' });
        await fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        setMessage({ type: 'error', text: 'Error deleting user: ' + error.message });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSendPasswordReset = async (user) => {
    if (!canSendPasswordReset) {
      setMessage({ type: 'error', text: 'You do not have permission to send password resets' });
      return;
    }

    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, user.email);

      await TransactionLogger.logTransaction(
        'UPDATE',
        'USER',
        user.id,
        null,
        { action: 'password_reset_sent' },
        currentUser.uid,
        currentUser.email
      );

      setMessage({ type: 'success', text: `Password reset email sent to ${user.email}` });
    } catch (error) {
      console.error('Error sending password reset:', error);
      setMessage({ type: 'error', text: 'Error sending password reset: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleMakeUserAdmin = async (email) => {
    if (!canEditUser) {
      setMessage({ type: 'error', text: 'You do not have permission to edit users' });
      return;
    }

    try {
      setLoading(true);
      const result = await UserManagementUtils.makeUserAdmin(email, currentUser.uid, currentUser.email);
      setMessage({ type: 'success', text: result.message });
      await fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error making user admin:', error);
      setMessage({ type: 'error', text: 'Error making user admin: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckClymusaStatus = async () => {
    try {
      setLoading(true);
      const user = await checkClymusaStatus();
      if (user) {
        setMessage({ 
          type: 'info', 
          text: `clymusa@gmail.com found - Role: ${user.role}, Name: ${user.firstName} ${user.lastName}` 
        });
      } else {
        setMessage({ type: 'warning', text: 'clymusa@gmail.com not found in the system' });
      }
    } catch (error) {
      console.error('Error checking user status:', error);
      setMessage({ type: 'error', text: 'Error checking user status: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleMakeAllUsersAdmin = async () => {
    if (!canEditUser) {
      setMessage({ type: 'error', text: 'You do not have permission to edit users' });
      return;
    }

    if (!window.confirm('WARNING: This will make ALL users administrators. Are you sure you want to continue?')) {
      return;
    }

    try {
      setLoading(true);
      const result = await makeAllUsersAdmin(currentUser.uid, currentUser.email);
      
      setMessage({ 
        type: 'success', 
        text: `Made all users admin! Updated: ${result.updatedUsers.length}, Already admin: ${result.alreadyAdmin.length}, Errors: ${result.errors.length}` 
      });
      
      await fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error making all users admin:', error);
      setMessage({ type: 'error', text: 'Error making all users admin: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRevertAllUsers = async () => {
    if (!canEditUser) {
      setMessage({ type: 'error', text: 'You do not have permission to edit users' });
      return;
    }

    if (!window.confirm('This will revert all users back to organizer role (except you). Are you sure?')) {
      return;
    }

    try {
      setLoading(true);
      const result = await revertAllUsersToOrganizer(currentUser.uid, currentUser.uid, currentUser.email);
      
      setMessage({ 
        type: 'success', 
        text: `Reverted users! Reverted: ${result.revertedUsers.length}, Kept as admin: ${result.keptAsAdmin.length}, Errors: ${result.errors.length}` 
      });
      
      await fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error reverting all users:', error);
      setMessage({ type: 'error', text: 'Error reverting all users: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAllUsersStatus = async () => {
    try {
      setLoading(true);
      const status = await getUsersAdminStatus();
      
      setMessage({ 
        type: 'info', 
        text: `User Status - Total: ${status.totalUsers}, Admins: ${status.admins.length}, Organizers: ${status.organizers.length}, Other: ${status.other.length}` 
      });
    } catch (error) {
      console.error('Error checking users status:', error);
      setMessage({ type: 'error', text: 'Error checking users status: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generateRandomPassword();
    setPasswordData({
      newPassword,
      confirmPassword: newPassword
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'organizer': return 'primary';
      default: return 'default';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    }
    return new Date(timestamp).toLocaleDateString();
  };

  if (!canManageUsers) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You do not have permission to manage users. Contact an administrator.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {getEmergencyAdminWarning(userProfile)}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">User Management</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchUsers}
            disabled={loading}
          >
            Refresh
          </Button>
          {canCreateUser && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateUser}
              disabled={loading}
            >
              Add User
            </Button>
          )}
        </Box>
      </Box>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      {/* Quick Admin Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Admin Actions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Make specific users administrators
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<Security />}
              onClick={() => handleMakeUserAdmin('clymusa@gmail.com')}
              disabled={loading}
              color="primary"
            >
              Make clymusa@gmail.com Admin
            </Button>
            <Button
              variant="outlined"
              startIcon={<Person />}
              onClick={handleCheckClymusaStatus}
              disabled={loading}
              color="info"
            >
              Check clymusa@gmail.com Status
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6" gutterBottom color="warning.main">
            ⚠️ Temporary Admin Actions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use these functions carefully - they affect ALL users in the system
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<Security />}
              onClick={handleMakeAllUsersAdmin}
              disabled={loading}
              color="warning"
            >
              Make ALL Users Admin
            </Button>
            <Button
              variant="outlined"
              startIcon={<Person />}
              onClick={handleRevertAllUsers}
              disabled={loading}
              color="secondary"
            >
              Revert All to Organizer
            </Button>
            <Button
              variant="outlined"
              startIcon={<Person />}
              onClick={handleCheckAllUsersStatus}
              disabled={loading}
              color="info"
            >
              Check All Users Status
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
        <Typography variant="h6" gutterBottom>
          System Users ({users.length})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage user accounts, roles, and permissions. Note: User creation requires Firebase Admin SDK.
        </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      {formatUserName(user, { showIcon: true })}
                    </TableCell>
                    <TableCell>
                      {formatUserEmail(user, { showIcon: true })}
                    </TableCell>
                    <TableCell>
                      {formatUserRole(user, { showIcon: true })}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Business sx={{ mr: 1, color: 'text.secondary' }} />
                        {user.organizationName || 'N/A'}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        {canEditUser && (
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              onClick={() => handleEditUser(user)}
                              disabled={loading}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canSendPasswordReset && (
                          <Tooltip title="Send Password Reset">
                            <IconButton
                              size="small"
                              onClick={() => handleSendPasswordReset(user)}
                              disabled={loading}
                            >
                              <LockReset />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canEditUser && user.role !== 'admin' && (
                          <Tooltip title="Make Admin">
                            <IconButton
                              size="small"
                              onClick={() => handleMakeUserAdmin(user.email)}
                              disabled={loading}
                              color="primary"
                            >
                              <Security />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDeleteUser && user.id !== currentUser?.uid && (
                          <Tooltip title="Delete User">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteUser(user)}
                              disabled={loading}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* User Edit/Create Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Create User'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={editingUser} // Can't change email for existing users
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    label="Role"
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <MenuItem value="admin">Administrator</MenuItem>
                    <MenuItem value="organizer">Organizer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {editingUser && canChangePassword && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Change Password
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      onClick={handleGeneratePassword}
                      startIcon={<Lock />}
                      sx={{ mb: 2 }}
                    >
                      Generate Random Password
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}
